import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import {
  emptyFamilyHistory,
  validateFamilyHistorySection,
  type FamilyHistoryInput,
} from '@helios/domain';
import {
  qPatientFamilyHistory,
  qPatientFamilyMembers,
  qPatientFamilyMemberAllergies,
  qPatientFamilyMemberMedications,
  groupFamilyHistory,
  familyHistoryVMToInput,
  buildFamilyHistoryInserts,
  FAMILY_HISTORY_TABLES,
  type PatientFamilyHistoryRow,
  type PatientFamilyMemberRow,
  type PatientFamilyMemberAllergyRow,
  type PatientFamilyMemberMedicationRow,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncQuery, usePowerSyncDb } from '../adapters/powersync';
import { FamilyHistoryEditor } from '../pages/patient/FamilyHistoryEditor';

/**
 * PRD.267 — edit a patient's Family Allergies & Medications from the sidebar.
 *
 * Reuses the standardized FamilyHistoryEditor. Saving is a soft-delete-then
 * reinsert "replace all" across the four `patient_family_*` tables — family
 * records never touch patient_allergies / patient_medications.
 */
export function EditFamilyHistoryDrawer({
  open,
  onOpenChange,
  brandTokens,
  patientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandTokens: Record<string, string>;
  patientId: string;
}) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: historyRows = [] } = usePowerSyncQuery<PatientFamilyHistoryRow>(
    qPatientFamilyHistory(patientId),
  );
  const { data: memberRows = [] } = usePowerSyncQuery<PatientFamilyMemberRow>(
    qPatientFamilyMembers(patientId),
  );
  const { data: allergyRows = [] } = usePowerSyncQuery<PatientFamilyMemberAllergyRow>(
    qPatientFamilyMemberAllergies(patientId),
  );
  const { data: medicationRows = [] } = usePowerSyncQuery<PatientFamilyMemberMedicationRow>(
    qPatientFamilyMemberMedications(patientId),
  );

  const seed = useMemo<FamilyHistoryInput>(() => {
    const vm = groupFamilyHistory(historyRows[0], memberRows, allergyRows, medicationRows);
    return vm.members.length > 0 || vm.historyStatus !== 'NOT_ASKED'
      ? familyHistoryVMToInput(vm)
      : emptyFamilyHistory();
  }, [historyRows, memberRows, allergyRows, medicationRows]);

  const [draft, setDraft] = useState<FamilyHistoryInput>(emptyFamilyHistory());
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const didHydrateRef = useRef(false);

  // Hydrate once on open from the loaded data; don't clobber while editing.
  useEffect(() => {
    if (open && !didHydrateRef.current) {
      setDraft(seed);
      setShowErrors(false);
      didHydrateRef.current = true;
    }
    if (!open) didHydrateRef.current = false;
  }, [open, seed]);

  // Per-row validation only (section is not force-required when editing here).
  const validation = useMemo(
    () => validateFamilyHistorySection(draft, { required: false }),
    [draft],
  );

  const handleSave = async () => {
    if (saving || !db) return;
    if (!validation.ok) {
      setShowErrors(true);
      return;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const genId = () =>
        crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      // Replace-all: soft-delete existing active family rows for this patient…
      for (const table of FAMILY_HISTORY_TABLES) {
        await db.execute(
          `UPDATE ${table} SET deleted_at = ?, updated_at = ? WHERE patient_id = ? AND deleted_at IS NULL`,
          [now, now, patientId],
        );
      }
      // …then insert the new set with fresh ids.
      const rows = buildFamilyHistoryInserts(draft, { patientId, orgId, genId, now });
      for (const { table, cols } of rows) {
        const keys = Object.keys(cols);
        const placeholders = keys.map(() => '?').join(', ');
        await db.execute(
          `INSERT INTO ${table} (${keys.join(', ')}) VALUES (${placeholders})`,
          keys.map((k) => cols[k]),
        );
      }
      onOpenChange(false);
    } catch (err) {
      console.error('[EditFamilyHistory] Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Family History"
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-6, 24px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5, 20px)',
        }}
      >
        <FamilyHistoryEditor
          value={draft}
          onChange={setDraft}
          showErrors={showErrors}
          errors={validation.errors}
        />
      </div>
    </Drawer>
  );
}
